import React, { useState } from "react";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "./ui";
import { WEEKDAYS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Copy, Check, Loader2, Plus, Trash2 } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { WaitlistSettingsTab } from "@/components/waitlist/WaitlistSettingsTab";

function ResourcesTab({ t }) {
    const qc = useQueryClient();
    const [name, setName] = useState("");
    const { data: resources } = useQuery({ queryKey: ["resources"], queryFn: async () => (await api.get("/resources")).data });
    const add = async () => {
        if (!name.trim()) return;
        try { await api.post("/resources", { name: name.trim() }); setName(""); qc.invalidateQueries({ queryKey: ["resources"] }); toast.success(t("common.saved")); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const remove = async (id) => { await api.delete(`/resources/${id}`); qc.invalidateQueries({ queryKey: ["resources"] }); };
    return (
        <div className="max-w-xl space-y-4 rounded-2xl border border-border/70 bg-card p-6" data-testid="resources-tab">
            <div className="flex gap-2">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("sched.resourceName")} data-testid="resource-name" onKeyDown={(e) => e.key === "Enter" && add()} />
                <Button onClick={add} className="rounded-full" data-testid="add-resource"><Plus className="mr-1 h-4 w-4" />{t("common.add")}</Button>
            </div>
            {(resources || []).length === 0 ? <p className="text-sm text-muted-foreground">{t("sched.noResources")}</p> : (
                <ul className="divide-y divide-border/60">
                    {resources.map((r) => (
                        <li key={r.id} className="flex items-center justify-between py-2 text-sm" data-testid={`resource-${r.id}`}>
                            <span>{r.name}</span>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => remove(r.id)} data-testid={`delete-resource-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default function Settings() {
    const { t } = useI18n();
    const { salon, setSalon, refreshMe } = useAuth();
    const [form, setForm] = useState({
        name: salon?.name || "", address: salon?.address || "", phone: salon?.phone || "",
        email: salon?.email || "", booking_enabled: salon?.booking_enabled ?? true,
        opening_hours: salon?.opening_hours || {},
    });
    const [saving, setSaving] = useState(false);
    const [copied, setCopied] = useState(false);

    const bookingUrl = `${window.location.origin}/book/${salon?.slug}`;

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch("/salon", form);
            setSalon(data);
            toast.success(t("common.saved"));
        } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
    };

    const setHour = (day, field, val) => {
        setForm((f) => ({ ...f, opening_hours: { ...f.opening_hours, [day]: { ...f.opening_hours[day], [field]: val } } }));
    };

    const copy = () => { navigator.clipboard.writeText(bookingUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); };

    return (
        <div>
            <PageHeader title={t("settings.title")} action={<Button onClick={save} disabled={saving} className="rounded-full" data-testid="save-settings">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save")}</Button>} />
            <Tabs defaultValue="salon">
                <TabsList>
                    <TabsTrigger value="salon" data-testid="tab-salon">{t("settings.salon")}</TabsTrigger>
                    <TabsTrigger value="hours" data-testid="tab-hours">{t("settings.openingHours")}</TabsTrigger>
                    <TabsTrigger value="booking" data-testid="tab-booking">{t("settings.booking")}</TabsTrigger>
                    <TabsTrigger value="resources" data-testid="tab-resources">{t("sched.resources")}</TabsTrigger>
                    <TabsTrigger value="waitlist" data-testid="tab-waitlist">{t("wl.settings.title")}</TabsTrigger>
                </TabsList>

                <TabsContent value="waitlist"><WaitlistSettingsTab t={t} /></TabsContent>

                <TabsContent value="resources"><ResourcesTab t={t} /></TabsContent>

                <TabsContent value="salon">
                    <div className="max-w-xl space-y-4 rounded-2xl border border-border/70 bg-card p-6">
                        <div><Label>{t("auth.salonName")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="settings-name" className="mt-1.5" /></div>
                        <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" /></div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label>{t("common.phone")}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
                            <div><Label>{t("common.email")}</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="hours">
                    <div className="max-w-xl space-y-2 rounded-2xl border border-border/70 bg-card p-6">
                        {WEEKDAYS.map((d, i) => {
                            const h = form.opening_hours[String(i)] || { open: "09:00", close: "18:00", closed: false };
                            return (
                                <div key={i} className="flex items-center gap-3 py-1.5">
                                    <span className="w-12 text-sm font-medium">{d}</span>
                                    <Switch checked={!h.closed} onCheckedChange={(v) => setHour(String(i), "closed", !v)} data-testid={`hours-toggle-${i}`} />
                                    {h.closed ? <span className="text-sm text-muted-foreground">{t("settings.closed")}</span> : (
                                        <>
                                            <Input type="time" value={h.open} onChange={(e) => setHour(String(i), "open", e.target.value)} className="w-32" />
                                            <span>–</span>
                                            <Input type="time" value={h.close} onChange={(e) => setHour(String(i), "close", e.target.value)} className="w-32" />
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </TabsContent>

                <TabsContent value="booking">
                    <div className="max-w-xl space-y-4 rounded-2xl border border-border/70 bg-card p-6">
                        <div className="flex items-center justify-between">
                            <Label>{t("settings.bookingEnabled")}</Label>
                            <Switch checked={form.booking_enabled} onCheckedChange={(v) => setForm({ ...form, booking_enabled: v })} data-testid="booking-toggle" />
                        </div>
                        <div>
                            <Label>{t("settings.bookingLink")}</Label>
                            <div className="mt-1.5 flex gap-2">
                                <Input readOnly value={bookingUrl} data-testid="booking-link" className="font-mono text-sm" />
                                <Button variant="outline" onClick={copy} className="shrink-0 rounded-lg" data-testid="copy-link">{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</Button>
                            </div>
                        </div>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
